/* Presentation grouping never merges cart data. Every mutation uses its original line key. */
(() => {
  'use strict';
  if (customElements.get('sf-cart-page')) return;
  class CompactCart extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.busy = false;
      this.uncertain = false;
      this.ar = this.getAttribute('lang') === 'ar';
      this.click = this.onClick.bind(this);
      this.change = this.onChange.bind(this);
      this.submit = this.onSubmit.bind(this);
      this.addEventListener('click', this.click);
      this.addEventListener('change', this.change);
      this.addEventListener('submit', this.submit);
      this.groupLines();
      setTimeout(() => this.syncGifts(), 0);
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.click);
      this.removeEventListener('change', this.change);
      this.removeEventListener('submit', this.submit);
      this._ready = false;
    }
    text(ar, en) { return this.ar ? ar : en; }
    groupLines() {
      const list = this.querySelector('[data-sfc-items]');
      if (!list) return;
      const groups = new Map();
      for (const line of list.querySelectorAll(':scope > [data-sfc-line]')) {
        if (line.dataset.groupAllowed !== 'true') continue;
        const key = line.dataset.group;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(line);
      }
      for (const [key, lines] of groups) {
        if (lines.length < 2) continue;
        const group = document.createElement('article');
        group.className = 'sfc-group';
        const head = lines[0].querySelector('[data-sfc-head]').cloneNode(true);
        const details = document.createElement('details');
        details.className = 'sfc-group-details'; details.dataset.groupDetails = key;
        const summary = document.createElement('summary'); summary.className = 'sfc-group-summary';
        const quantity = document.createElement('span');
        quantity.textContent = `${lines[0].dataset.groupQty} ${this.text('قطع', 'items')}`;
        const edit = document.createElement('small');
        edit.textContent = this.text('تعديل الكميات والتفاصيل ⌄', 'Edit quantities & details ⌄');
        quantity.append(edit);
        const total = document.createElement('strong');
        const money = document.createElement('bdi'); money.textContent = lines[0].dataset.groupTotal;
        total.append(money); summary.append(quantity, total); details.append(summary);
        const note = document.createElement('p'); note.className = 'sfc-group-note';
        note.textContent = this.text('إضافات لنفس الصنف؛ بيانات كل إضافة محفوظة.', 'Same item, with each addition kept separately.');
        group.append(head, details, note); lines[0].before(group);
        for (const line of lines) details.append(line);
      }
    }
    state() {
      const data = {open: [], fields: {}};
      for (const d of this.querySelectorAll('details[open]')) {
        data.open.push(d.hasAttribute('data-sfc-options') ? 'options' : (d.dataset.groupDetails || d.dataset.lineDetails));
      }
      for (const kind of ['note', 'vin', 'discount']) {
        const input = this.querySelector(`[data-sfc-${kind}]`);
        if (input && (kind === 'discount' || input.value !== input.dataset.initial)) data.fields[kind] = input.value;
      }
      return data;
    }
    restore(snapshot) {
      for (const [key, value] of Object.entries(snapshot.fields)) {
        const el = this.querySelector(`[data-sfc-${key}]`); if (el) el.value = value;
      }
      for (const d of this.querySelectorAll('details')) {
        const id = d.hasAttribute('data-sfc-options') ? 'options' : (d.dataset.groupDetails || d.dataset.lineDetails);
        if (snapshot.open.includes(id)) d.open = true;
      }
    }
    setBusy(value) {
      this.busy = value; this.setAttribute('aria-busy', String(value));
      for (const el of this.querySelectorAll('[data-sfc-pay],[data-sfc-qty],[data-sfc-plus],[data-sfc-minus],[data-sfc-apply],[data-sfc-remove-code]')) {
        if (value) { el.dataset.sfcWasDisabled = String(el.disabled); el.disabled = true; }
        else if (el.dataset.sfcWasDisabled !== undefined) { el.disabled = el.dataset.sfcWasDisabled === 'true'; delete el.dataset.sfcWasDisabled; }
      }
      const pay = this.querySelector('[data-sfc-pay]'); if (pay && (this.uncertain || this.dataset.blocked === 'true')) pay.disabled = true;
      const status = this.querySelector('[data-sfc-status]');
      if (status) status.textContent = value ? this.text('جارٍ تحديث السلة…', 'Updating cart…') : this.text('تم تحديث السلة.', 'Cart updated.');
    }
    showError(message, uncertain = false) {
      this.uncertain = uncertain;
      const error = this.querySelector('[data-sfc-error]');
      if (error) { error.hidden = !message; error.textContent = message || ''; }
      const reload = this.querySelector('[data-sfc-reload]'); if (reload) reload.hidden = !uncertain;
      const pay = this.querySelector('[data-sfc-pay]'); if (pay) pay.disabled = uncertain || this.busy || this.dataset.blocked === 'true';
    }
    ajaxURL(path) {
      const url = new URL(path, location.origin);
      if (!url.pathname.endsWith('.js')) url.pathname = url.pathname.replace(/\/$/, '') + '.js';
      return url.href;
    }
    async fetchJSON(url, body) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(this.ajaxURL(url), {method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json','Accept':'application/json'}, body:JSON.stringify(body), signal:controller.signal});
        const result = await response.json();
        if (!response.ok || result.errors || result.status >= 400) {
          const value = result.description || result.message || result.errors;
          const message = typeof value === 'string' ? value : this.text('تعذر تحديث السلة. تحقق من الكمية المتاحة.', 'Unable to update cart. Check available quantity.');
          const error = new Error(message); error.serverResponse = true; throw error;
        }
        return result;
      } finally { clearTimeout(timer); }
    }
    async sectionHTML() {
      const url = new URL(this.dataset.cartUrl, location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, {cache:'no-store', credentials:'same-origin', signal:controller.signal});
        if (!response.ok) throw new Error('Unable to refresh cart');
        return await response.text();
      } finally { clearTimeout(timer); }
    }
    async render(result, snapshot, focusLine) {
      const source = result?.sections?.[this.dataset.sectionId] || await this.sectionHTML();
      const doc = new DOMParser().parseFromString(source, 'text/html');
      const next = doc.querySelector('sf-cart-page');
      if (!next?.querySelector('[data-sfc-content]')) throw new Error('Cart section unavailable');
      // Preserve changes typed into optional fields while a request was in flight.
      const recent = this.state();
      snapshot.fields = {...snapshot.fields, ...recent.fields};
      snapshot.open = recent.open;
      this.dataset.empty = next.dataset.empty;
      this.dataset.itemCount = next.dataset.itemCount;
      this.dataset.blocked = next.dataset.blocked || 'false';
      this.querySelector('[data-sfc-content]').replaceWith(next.querySelector('[data-sfc-content]'));
      this.groupLines(); this.restore(snapshot);
      if (this.busy) this.setBusy(true);
      const currentCount = Number(result?.item_count ?? next.dataset.itemCount);
      if (Number.isFinite(currentCount)) {
        for (const badge of document.querySelectorAll('[data-sf26-count]')) { badge.textContent = currentCount > 99 ? '99+' : String(currentCount); badge.hidden = currentCount === 0; }
        for (const span of document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]')) span.textContent = currentCount;
      }
      if (result && Number.isFinite(result.item_count)) {
        for (const span of document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]')) span.textContent = result.item_count;
        document.dispatchEvent(new CustomEvent('cart:updated',{bubbles:true,detail:{cart:result}}));
        const drawer = document.querySelector('cart-drawer');
        if (typeof drawer?.renderContentsFromHTML === 'function' && result.sections?.['cart-drawer']) {
          try { drawer.renderContentsFromHTML(result.sections); } catch (_) { /* Page is already current. */ }
        }
      }
      if (focusLine) requestAnimationFrame(() => {
        const target = this.querySelector(`[data-sfc-line][data-line="${focusLine}"] [data-sfc-qty]`);
        if (target && target.getClientRects().length) target.focus({preventScroll:true});
        else this.querySelector('h1')?.focus({preventScroll:true});
      });
    }
    async mutate(url, payload, focusLine, attemptedCode = '') {
      if (this.busy || this.uncertain) return;
      const snapshot = this.state(); this.showError(''); this.setBusy(true);
      try {
        const result = await this.fetchJSON(url, {...payload, sections:[this.dataset.sectionId,'cart-icon-bubble','cart-drawer'], sections_url:location.pathname});
        await this.render(result, snapshot, focusLine);
        if (attemptedCode) {
          const code = result.discount_codes?.find(d => d.code.toLowerCase() === attemptedCode.toLowerCase());
          if (code && !code.applicable) this.showError(this.text('كود الخصم غير صالح لهذه السلة.', 'This discount does not apply to your cart.'));
          else { const input = this.querySelector('[data-sfc-discount]'); if(input) input.value = ''; }
        }
        this.uncertain = false;
      } catch (error) {
        if (error.serverResponse) {
          try { await this.render(null, snapshot, focusLine); this.showError(error.message); }
          catch (_) { this.showError(this.text('تعذر تأكيد حالة السلة. أعد تحميلها قبل الدفع.', 'Cart state could not be confirmed. Reload before checkout.'), true); }
        } else {
          this.showError(this.text('تعذر تأكيد التحديث. أعد تحميل السلة قبل الدفع.', 'Update could not be confirmed. Reload cart before checkout.'), true);
        }
      } finally { this.setBusy(false); this.syncGifts(); }
    }
    updateLine(line, nextQuantity) {
      if (!line || this.busy || this.uncertain) return;
      const input = line.querySelector('[data-sfc-qty]');
      if (!input || (input.readOnly && Number(nextQuantity) !== 0)) return;
      if (Number(nextQuantity) === 0 && !line.querySelector('[data-sfc-remove]')) return;
      const qty = Number(nextQuantity), min = Number(input.dataset.min || 1), step = Number(input.step || 1);
      const max = input.max ? Number(input.max) : Infinity;
      if (!Number.isInteger(qty) || qty < 0 || (qty > 0 && (qty < min || qty % step !== 0 || qty > max))) {
        input.value = input.dataset.saved;
        this.showError(this.text('تحقق من الكمية: الحد الأدنى ', 'Check quantity: minimum ') + min + this.text('، والمضاعفات ', ', increments ') + step + (Number.isFinite(max) ? this.text('، والحد الأعلى ', ', maximum ') + max : ''));
        return;
      }
      if (qty === Number(input.dataset.saved)) return;
      this.mutate(this.dataset.changeUrl, {id:line.dataset.key, quantity:qty}, line.dataset.line);
    }
    codes() { try{return JSON.parse(this.querySelector('[data-sfc-codes]')?.dataset.codes || '[]');}catch(_){return [];} }
    onClick(event) {
      const action = event.target.closest('[data-sfc-minus],[data-sfc-plus],[data-sfc-remove],[data-sfc-apply],[data-sfc-remove-code],[data-sfc-reload]');
      if (!action || !this.contains(action)) return;
      if (action.matches('a') && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
      event.preventDefault();
      if (action.matches('[data-sfc-reload]')) { this.reload(); return; }
      if (this.busy || this.uncertain) return;
      const line = action.closest('[data-sfc-line]');
      const input = line?.querySelector('[data-sfc-qty]');
      if (action.matches('[data-sfc-remove]')) { this.updateLine(line, 0); return; }
      if (action.matches('[data-sfc-minus],[data-sfc-plus]')) {
        const delta = Number(input.step || 1) * (action.matches('[data-sfc-minus]') ? -1 : 1);
        this.updateLine(line, Math.max(Number(input.dataset.min || 1),Number(input.value) + delta)); return;
      }
      const code = action.matches('[data-sfc-apply]') ? this.querySelector('[data-sfc-discount]').value.trim() : '';
      if (action.matches('[data-sfc-apply]') && !code) { this.querySelector('[data-sfc-discount]').focus(); return; }
      const existing = this.codes();
      const codes = code ? [...existing.filter(v=>v.toLowerCase()!==code.toLowerCase()),code] : existing.filter(v=>v!==action.dataset.sfcRemoveCode);
      this.mutate(this.dataset.updateUrl, {discount:codes.join(',')}, null, code);
    }
    onChange(event) {
      if (event.target.matches('[data-sfc-qty]')) this.updateLine(event.target.closest('[data-sfc-line]'),event.target.value);
    }
    onSubmit(event) {
      if (event.target.matches('[data-sfc-line-form]')) {
        event.preventDefault(); const line = event.target.closest('[data-sfc-line]'); this.updateLine(line,line.querySelector('[data-sfc-qty]').value); return;
      }
      if (!event.target.matches('[data-sfc-checkout]')) return;
      // Do not proceed with unconfirmed quantities; ordinary native POST handles note and VIN.
      if (this.busy || this.uncertain || this.dataset.blocked === 'true') {event.preventDefault();return;}
      const dirty = [...this.querySelectorAll('[data-sfc-qty]')].find(el=>el.value!==el.dataset.saved);
      if (dirty) { event.preventDefault(); this.updateLine(dirty.closest('[data-sfc-line]'),dirty.value); return; }
      // Pressing Enter in the coupon field applies it instead of accidentally checking out.
      if (document.activeElement?.matches('[data-sfc-discount]')) {
        event.preventDefault(); this.querySelector('[data-sfc-apply]')?.click();
      }
    }
    // Cart gifts (owner 2 Nov 2026): the section says in data-sync which gift lines to add or remove; one pass at a time,
    // at most twice for the same plan, then the section is fetched again so the page shows the real cart.
    async syncGifts() {
      if (this.busy || this.uncertain || this._gifting) return;
      const box = this.querySelector('[data-sfc-gifts]');
      if (!box) return;
      let plan;
      try { plan = JSON.parse(box.dataset.sync || '{}'); } catch (_) { return; }
      const add = Array.isArray(plan.add) ? plan.add : [], remove = Array.isArray(plan.remove) ? plan.remove : [];
      if (!add.length && !remove.length) return;
      const sig = JSON.stringify(plan);
      this._giftTries = this._giftTries || {};
      this._giftTries[sig] = (this._giftTries[sig] || 0) + 1;
      if (this._giftTries[sig] > 2) return;
      this._gifting = true;
      const snapshot = this.state(); this.setBusy(true);
      const base = String(this.dataset.cartUrl || '/cart').replace(/\/$/, '');
      try {
        if (remove.length) { const updates = {}; for (const key of remove) updates[key] = 0; await this.fetchJSON(base + '/update', {updates}); }
        if (add.length) await this.fetchJSON(base + '/add', {items: add.map(g => ({id: Number(g.id), quantity: 1, properties: {_sf_gift: String(g.tier)}}))});
      } catch (_) { /* the fresh section below shows what the cart really holds */ }
      try { await this.render(null, snapshot, null); }
      catch (_) { this.showError(this.text('تعذر تحديث هدايا السلة. أعد تحميل السلة قبل الدفع.', 'Cart gifts could not be updated. Reload the cart before checkout.'), true); }
      finally { this._gifting = false; this.setBusy(false); }
      this.syncGifts();
    }
    async reload() {
      if (this.busy) return;
      const snapshot = this.state(); this.setBusy(true);
      try {await this.render(null,snapshot,null);this.uncertain=false;this.showError('');}
      catch(_){this.showError(this.text('تعذر تحميل السلة. أعد المحاولة.', 'Unable to load cart. Please retry.'),true);}
      finally{this.setBusy(false);this.syncGifts();}
    }
  }
  customElements.define('sf-cart-page', CompactCart);
})();
